import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * Custom element for displaying recently viewed products.
 * Reads product handles from localStorage and fetches product cards via the Section Rendering API.
 * The section is rendered in "card mode" when fetched on a product page, using the block settings
 * configured in the theme editor for consistent styling.
 */
class RecentlyViewedProducts extends HTMLElement {
  /**
   * The observer for lazy loading the section
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#loadRecentlyViewed();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    // Use IntersectionObserver for lazy loading
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#activeFetch?.abort();
  }

  /**
   * Load and render the recently viewed products
   */
  async #loadRecentlyViewed() {
    const currentProductHandle = this.dataset.currentProductHandle || '';
    const maxProducts = this.dataset.maxProducts || '4';
    const sectionId = this.dataset.sectionId || '';
    const layoutType = this.dataset.layoutType || 'grid';
    const carouselOnMobile = this.dataset.carouselOnMobile === 'true';
    const iconsStyle = this.dataset.iconsStyle || 'arrow';
    const iconsShape = this.dataset.iconsShape || 'none';
    const sectionWidth = this.dataset.sectionWidth || 'page-width';
    const columns = parseInt(this.dataset.columns || '4', 10);

    // Get product handles from localStorage
    let productHandles = RecentlyViewed.getProducts();

    // Filter out the current product if on a product page
    if (currentProductHandle) {
      productHandles = productHandles.filter((handle) => handle !== currentProductHandle);
    }

    // Limit to max products
    const limit = parseInt(maxProducts, 10) || 4;
    productHandles = productHandles.slice(0, limit);

    // If no products, hide the section
    if (productHandles.length === 0) {
      this.#hideSection();
      return;
    }

    this.classList.add('is-loading');

    try {
      // Fetch product cards for each product handle
      // Uses the same section (recently-viewed-products) which renders in "card mode"
      // when fetched on a product page, applying all block settings from the theme editor
      const productCards = await this.#fetchProductCards(productHandles, sectionId);

      if (productCards.length === 0) {
        this.#hideSection();
        return;
      }

      // Render the products
      this.#renderProducts(productCards, layoutType, carouselOnMobile, {
        iconsStyle,
        iconsShape,
        sectionWidth,
        columns,
      });
    } catch (error) {
      console.error('[RecentlyViewed] Error loading products:', error);
      this.#hideSection();
    } finally {
      this.classList.remove('is-loading');
    }
  }

  /**
   * Fetch product cards for the given product handles using Section Rendering API.
   * Each product is fetched by loading this section on that product's page,
   * which triggers "card mode" and renders the product card with theme editor settings.
   *
   * @param {string[]} productHandles - Array of product handles
   * @param {string} sectionId - The section ID to use for consistent block settings
   * @returns {Promise<string[]>} - Array of HTML strings for product cards
   */
  async #fetchProductCards(productHandles, sectionId) {
    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    const productCards = [];
    // @ts-ignore - Shopify global is defined in the Shopify theme
    const rootUrl = window.Shopify?.routes?.root || '/';

    // Fetch products in parallel using Section Rendering API
    // When this section is rendered on a product page, it enters "card mode"
    // and outputs just the product card with all block settings applied
    const signal = this.#activeFetch.signal;
    const fetchPromises = productHandles.map(async (productHandle) => {
      try {
        // Use the Section Rendering API with THIS section
        // The section detects it's on a product page and renders just the card
        const url = `${rootUrl}products/${productHandle}?section_id=${sectionId}`;

        const response = await fetch(url, { signal });

        if (!response.ok) {
          console.warn(`[RecentlyViewed] Failed to fetch product ${productHandle}: ${response.status}`);
          return null;
        }

        const html = await response.text();

        // The section returns HTML wrapped in a shopify-section div
        // When in card mode, it returns: <div class="resource-list__item recently-viewed-card-wrapper">...</div>
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Find the card wrapper that the section outputs in card mode
        const cardWrapper = doc.querySelector('.recently-viewed-card-wrapper');
        if (cardWrapper) {
          return cardWrapper.innerHTML;
        }

        // Fallback: look for product-card-link or product-card directly
        const productCard = doc.querySelector('product-card-link, product-card');
        if (productCard) {
          return productCard.outerHTML;
        }

        console.warn('[RecentlyViewed] Could not find product card in response for', productHandle);
        return null;
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(`[RecentlyViewed] Error fetching product ${productHandle}:`, error);
        }
        return null;
      }
    });

    const results = await Promise.all(fetchPromises);

    // Filter out null results and return in order
    for (const result of results) {
      if (result) {
        productCards.push(result);
      }
    }

    this.#activeFetch = null;
    return productCards;
  }

  /**
   * Render the product cards into the list container
   * @param {string[]} productCards - Array of HTML strings for product cards
   * @param {string} layoutType - 'grid' or 'carousel'
   * @param {boolean} carouselOnMobile - Whether to show carousel on mobile
   * @param {object} [options] - Carousel options
   * @param {string} [options.iconsStyle] - The icon style (arrow, chevron, etc.)
   * @param {string} [options.iconsShape] - The icon shape (none, circle, square)
   * @param {string} [options.sectionWidth] - The section width (page-width, full-width)
   * @param {number} [options.columns] - Number of columns
   */
  #renderProducts(productCards, layoutType, carouselOnMobile, options = {}) {
    /** @type {HTMLElement | null} */
    const productList = this.querySelector('[ref="productList"]');
    /** @type {HTMLElement | null} */
    const mobileCarouselContainer = this.querySelector('[ref="mobileCarouselContainer"]');

    if (!productList) {
      console.warn('[RecentlyViewed] Product list container not found');
      return;
    }

    // Create list items HTML
    const listItemsHtml = productCards
      .map((card) => `<div class="resource-list__item">${card}</div>`)
      .join('');

    // Create slides for carousel
    const slides = productCards.map(
      (card, index) => this.#createSlideHtml(card, index, productCards.length)
    );

    try {
      if (layoutType === 'grid') {
        productList.innerHTML = listItemsHtml;
      } else if (layoutType === 'carousel') {
        // For carousel layout, we need to render with the carousel structure
        productList.innerHTML = this.#createCarouselHtml(slides, options);
      }

      // Handle mobile carousel if enabled
      if (carouselOnMobile && layoutType !== 'carousel' && mobileCarouselContainer) {
        mobileCarouselContainer.innerHTML = this.#createCarouselHtml(slides, options);
      }

      // Mark as having products - set immediately after innerHTML and use setAttribute as fallback
      productList.dataset.hasProducts = 'true';
      productList.setAttribute('data-has-products', 'true');

      // Mark the parent element - this ensures CSS visibility works
      this.dataset.hasProducts = 'true';
      this.setAttribute('data-has-products', 'true');

      // Initialize Klaviyo Back In Stock triggers for any sold-out products
      // The function is exposed globally by klaviyo-bis-trigger.liquid
      if (typeof window.initializeKlaviyoBisTriggers === 'function') {
        window.initializeKlaviyoBisTriggers(this);
      }
    } catch (error) {
      console.error('[RecentlyViewed] Error rendering products:', error);
      this.#hideSection();
    }
  }

  /**
   * Create a single slide HTML
   * @param {string} card - Product card HTML
   * @param {number} index - Slide index
   * @param {number} total - Total number of slides
   * @returns {string} - Slide HTML
   */
  #createSlideHtml(card, index, total) {
    return `
      <slideshow-slide
        ref="slides[]"
        aria-hidden="${index === 0 ? 'false' : 'true'}"
        style="view-timeline-name: --slide-${index};"
        class="resource-list__slide"
        slide-id="recently-viewed-slide-${index}"
      >
        <div class="resource-list__item">${card}</div>
      </slideshow-slide>
    `;
  }

  /**
   * Create carousel HTML structure using the theme's slideshow components
   * @param {string[]} slides - Array of slide HTML strings (already formatted)
   * @param {object} [options] - Carousel options
   * @param {string} [options.iconsStyle] - The icon style (arrow, chevron, etc.)
   * @param {string} [options.iconsShape] - The icon shape (none, circle, square)
   * @param {string} [options.sectionWidth] - The section width (page-width, full-width)
   * @param {number} [options.columns] - Number of columns
   * @returns {string} - Carousel HTML
   */
  #createCarouselHtml(slides, options = {}) {
    const { iconsStyle = 'arrow', iconsShape = 'none', sectionWidth = 'page-width' } = options;
    const slidesHtml = slides.join('');
    const slideCount = slides.length;

    // Generate timeline scope for animations
    const timelineScope = Array.from({ length: slideCount }, (_, i) => `--slide-${i}`).join(', ');

    // Determine gutters based on section width
    const gutters = sectionWidth === 'page-width' ? 'start end' : '';

    // Generate arrow controls if icons are enabled
    const arrowsHtml = iconsStyle !== 'none' ? this.#createArrowsHtml(iconsStyle, iconsShape) : '';

    return `
      <div class="resource-list__carousel" style="--gutter-slide-width: ${sectionWidth === 'page-width' ? 'var(--util-page-margin-offset)' : '0px'}; --slide-width-max: 300px;">
        <slideshow-component
          class="resource-list__carousel"
          style="--slideshow-timeline: ${timelineScope};"
          initial-slide="0"
          auto-hide-controls
          infinite
        >
          <slideshow-container ref="slideshowContainer">
            ${arrowsHtml}
            <slideshow-slides
              tabindex="-1"
              ref="scroller"
              ${gutters ? `gutters="${gutters}"` : ''}
            >
              ${slidesHtml}
            </slideshow-slides>
          </slideshow-container>
        </slideshow-component>
      </div>
    `;
  }

  /**
   * Create slideshow arrows HTML
   * @param {string} iconsStyle - The icon style (arrow, chevron, arrows_large, chevron_large)
   * @param {string} iconsShape - The icon shape (none, circle, square)
   * @returns {string} - Arrows HTML
   */
  #createArrowsHtml(iconsStyle, iconsShape) {
    const isChevron = iconsStyle.includes('chevron');
    const isLarge = iconsStyle.includes('large');
    const iconName = isChevron ? 'caret' : 'arrow';

    // SVG icons matching the theme's icons
    const arrowSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width)" vector-effect="non-scaling-stroke" d="M4.25 10h11.5m0 0-4-4m4 4-4 4"/></svg>`;
    const caretSvg = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11 5.5L7 9.5L3 5.5" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

    const svg = isChevron ? caretSvg : arrowSvg;
    const sizeClass = isLarge ? ' slideshow-control--large' : '';
    const shapeClass = iconsShape !== 'none' ? ` slideshow-control--shape-${iconsShape}` : '';

    return `
      <slideshow-arrows position="center">
        <button
          class="slideshow-control slideshow-control--previous${sizeClass}${shapeClass} slideshow-control--style-${iconsStyle} button button-unstyled button-unstyled--transparent flip-x"
          aria-label="Previous"
          on:click="/previous"
          ref="previous"
        >
          <span class="svg-wrapper icon-${iconName}">${svg}</span>
        </button>
        <button
          class="slideshow-control slideshow-control--next${sizeClass}${shapeClass} slideshow-control--style-${iconsStyle} button button-unstyled button-unstyled--transparent"
          aria-label="Next"
          on:click="/next"
          ref="next"
        >
          <span class="svg-wrapper icon-${iconName}">${svg}</span>
        </button>
      </slideshow-arrows>
    `;
  }

  /**
   * Hide the section when there are no products
   */
  #hideSection() {
    /** @type {HTMLElement | null} */
    const productList = this.querySelector('[ref="productList"]');
    if (productList) {
      productList.innerHTML = '';
      productList.dataset.hasProducts = 'false';
      productList.setAttribute('data-has-products', 'false');
    }
    // Mark the parent element - this is what the CSS uses to hide the section
    this.dataset.hasProducts = 'false';
    this.setAttribute('data-has-products', 'false');
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
